import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Target,
  Wind,
  MapPin,
  Flag,
  AlertTriangle,
  Lightbulb,
  Brain,
  Zap,
  Activity,
  Cpu,
  Settings,
} from "lucide-react";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile, ShotSituation, ShotAdvice } from "../types";
import { generateShotAdvice } from "../utils/calculations";
import { aiService } from "../services/aiService";
import { stripMarkdown } from "../utils/formatAIText";
import { recordProductEvent } from "../services/productEvents";

interface ShotAdvisorProps {
  userProfile: UserProfile | null;
}

const ShotAdvisor: React.FC<ShotAdvisorProps> = ({ userProfile }) => {
  const [situation, setSituation] = useState<ShotSituation>({
    lie: "fairway",
    slope: "flat",
    weather: "calm",
    distance: 150,
    pin: "middle",
    hazards: [],
  });

  const [advice, setAdvice] = useState<ShotAdvice | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Track whether user has run at least one analysis (enables auto-recalc)
  const [hasAnalyzedOnce, setHasAnalyzedOnce] = useState(false);
  // Track when inputs changed after a recommendation was shown
  const [inputsChanged, setInputsChanged] = useState(false);
  
  // NEW: Detailed wind and elevation state for accurate AI recommendations
  const [windSpeed, setWindSpeed] = useState<"no-wind" | "light" | "moderate" | "strong" | "very-strong">("no-wind");
  const [windDirection, setWindDirection] = useState<"headwind" | "tailwind" | "crosswind">("headwind");
  const [elevation, setElevation] = useState<string>("0");
  
  // REFS to guarantee fresh values are read (fixes stale closure bug)
  const windSpeedRef = useRef(windSpeed);
  const windDirectionRef = useRef(windDirection);
  const elevationRef = useRef(elevation);
  const situationRef = useRef(situation);
  
  // Keep refs in sync with state - ensures async functions always read latest values
  useEffect(() => { windSpeedRef.current = windSpeed; }, [windSpeed]);
  useEffect(() => { windDirectionRef.current = windDirection; }, [windDirection]);
  useEffect(() => { elevationRef.current = elevation; }, [elevation]);
  
  useEffect(() => {
    situationRef.current = situation;
  }, [situation]);
  
  // Auto-recalculate: when any input changes after the first analysis, re-run automatically
  // Uses a debounce so rapid changes (like typing yardage) don't fire multiple API calls
  const recalcTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track if inputs changed while analyzing (queue recalc for after)
  const pendingRecalcRef = useRef(false);

  const triggerAutoRecalc = useCallback(() => {
    if (!hasAnalyzedOnce) return;
    setInputsChanged(true);
    
    if (isAnalyzing) {
      // Queue a recalc for when the current analysis completes
      pendingRecalcRef.current = true;
      return;
    }
    
    // Debounce — wait 800ms after last change before firing
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    recalcTimerRef.current = setTimeout(() => {
      // Read latest values from refs to avoid stale closure
      setInputsChanged(false);
      pendingRecalcRef.current = false;
      // We call getAdvice via a wrapper that reads current state
      getAdviceFromRefs();
    }, 800);
  }, [hasAnalyzedOnce, isAnalyzing]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Trigger auto-recalc when wind, elevation, distance, lie, slope, pin, or weather change
  useEffect(() => {
    triggerAutoRecalc();
    return () => { if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current); };
  }, [windSpeed, windDirection, elevation, situation.distance, situation.lie, situation.slope, situation.pin, situation.weather, triggerAutoRecalc]);
  
  // AI Analysis animation states
  const [analysisPhase, setAnalysisPhase] = useState(0);
  const analysisMessages = [
    "Analyzing shot parameters...",
    "Calculating wind resistance...",
    "Evaluating elevation impact...",
    "Matching to your distances...",
    "Optimizing club selection...",
    "Generating recommendations..."
  ];
  
  // Cycle through analysis messages when analyzing
  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => {
        setAnalysisPhase(prev => (prev + 1) % analysisMessages.length);
      }, 600);
      return () => clearInterval(interval);
    } else {
      setAnalysisPhase(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analysisMessages is a constant
  }, [isAnalyzing]);

  const handleSituationChange = (field: keyof ShotSituation, value: any) => {
    setSituation((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  
  // Helper: reads latest values from refs for auto-recalc (avoids stale closures)
  const getAdviceFromRefs = () => {
    getAdvice(windSpeedRef.current, windDirectionRef.current, elevationRef.current, situationRef.current.distance);
  };

  // Pass values as parameters to guarantee fresh values at click time
  const getAdvice = async (
    inputWindSpeed: typeof windSpeed,
    inputWindDirection: typeof windDirection,
    inputElevation: string,
    inputDistance: number
  ) => {
    // Clear any existing advice first so animation shows
    setAdvice(null);
    setIsAnalyzing(true);
    
    // Force a render cycle so the animation actually displays before API call
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Track when we started for minimum animation time
    const startTime = Date.now();
    // First analysis: show full animation (2s). Auto-recalcs: shorter (1s) since user is already engaged
    const MIN_ANIMATION_MS = hasAnalyzedOnce ? 1000 : 2000;

    try {
      let shotAdvice: ShotAdvice;

      // Convert wind speed to mph - using INPUT values passed at click time
      const windSpeedMph = inputWindSpeed === "no-wind" ? 0 :
                           inputWindSpeed === "light" ? 5 :
                           inputWindSpeed === "moderate" ? 10 :
                           inputWindSpeed === "strong" ? 15 : 20; // very-strong
      
      // Parse elevation as a number
      const elevationFeet = parseInt(inputElevation) || 0;

      // Use AI for intelligent recommendations
      if (userProfile?.golfBag?.length) {
        const availableClubs = userProfile.golfBag.map((club) => club.type);
        
        // Build club distances array from golfBag
        const clubDistances = userProfile.golfBag
          .filter(club => club.distance && club.distance > 0)
          .map(club => ({ club: club.type, distance: club.distance! }));
        
        shotAdvice = await aiService.getAIShotAdvice({
          userProfile,
          situation: {
            ...situation,
            slope: elevationFeet === 0 ? "flat" : elevationFeet > 0 ? "uphill" : "downhill",
            weather: inputWindSpeed !== "no-wind" && inputWindSpeed !== "light" ? "windy" : "calm",
          },
          targetDistance: inputDistance,
          availableClubs,
          clubDistances,
          windConditions: {
            speed: windSpeedMph,
            direction: inputWindDirection,
            description: windSpeedMph === 0 ? "No wind" : `${windSpeedMph} mph ${inputWindDirection}`
          },
          elevationChange: elevationFeet
        });
      } else {
        // Fallback to rule-based advice
        shotAdvice = generateShotAdvice(situation, userProfile);
      }
      
      // Ensure animation shows for minimum time so user sees it
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_ANIMATION_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_ANIMATION_MS - elapsed));
      }

      setAdvice(shotAdvice);
      setHasAnalyzedOnce(true);
      void recordProductEvent('SHOT_ADVISOR_USED');
    } catch (error) {
      console.error(
        "AI shot advice failed, falling back to rule-based:",
        error,
      );

      // Fallback to rule-based advice
      const shotAdvice = generateShotAdvice(situation, userProfile);
      
      // Still wait for minimum animation time on error
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_ANIMATION_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_ANIMATION_MS - elapsed));
      }
      
      setAdvice(shotAdvice);
    }

    setIsAnalyzing(false);
    
    // If inputs changed while we were analyzing, run again with latest values
    if (pendingRecalcRef.current) {
      pendingRecalcRef.current = false;
      setTimeout(() => getAdviceFromRefs(), 100);
    }
  };

  const getClubDisplayName = (club: string): string => {
    const names: { [key: string]: string } = {
      driver: "Driver",
      "3-wood": "3-Wood",
      "5-wood": "5-Wood",
      "7-wood": "7-Wood",
      "2-hybrid": "2-Hybrid",
      "3-hybrid": "3-Hybrid",
      "4-hybrid": "4-Hybrid",
      "5-hybrid": "5-Hybrid",
      "3-iron": "3-Iron",
      "4-iron": "4-Iron",
      "5-iron": "5-Iron",
      "6-iron": "6-Iron",
      "7-iron": "7-Iron",
      "8-iron": "8-Iron",
      "9-iron": "9-Iron",
      pw: "Pitching Wedge",
      gw: "Gap Wedge",
      sw: "Sand Wedge",
      lw: "Lob Wedge",
      putter: "Putter",
    };
    return names[club] || club;
  };

  return (
    <>
      <SEO {...PAGE_SEO.shotAdvisor} url="/shot-advisor" />
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-green-500 text-white rounded-lg">
              <Target size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Shot Advisor</h1>
              <p className="text-gray-600 text-lg">Get contextual advice for any situation</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Situation Input */}
          <div className="space-y-6">
            <div className="card-feature" style={{ padding: "2rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "2rem",
                }}
              >
                <div
                  style={{
                    padding: "1rem",
                    borderRadius: "1.5rem",
                    background: "linear-gradient(135deg, #22c55e, #16a34a)",
                    color: "white",
                    boxShadow: "0 8px 25px rgba(34, 197, 94, 0.3)",
                  }}
                >
                  <MapPin size={24} />
                </div>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "800",
                    color: "var(--gray-900)",
                  }}
                >
                  🎯 Shot Situation
                </h2>
              </div>

              <div className="space-y-6">
                {/* Distance */}
                <div className="animate-fade-in">
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "var(--gray-700)",
                      marginBottom: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    📏 Distance to Pin
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="number"
                      value={situation.distance}
                      onChange={(e) =>
                        handleSituationChange(
                          "distance",
                          Number(e.target.value),
                        )
                      }
                      className="input"
                      placeholder="150"
                      style={{
                        paddingRight: "5rem",
                        fontSize: "1.25rem",
                        fontWeight: "700",
                        textAlign: "center",
                        background: "rgba(255, 255, 255, 0.9)",
                        backdropFilter: "blur(10px)",
                        border: "2px solid var(--gray-200)",
                        borderRadius: "1.5rem",
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--gray-500)",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        background: "var(--gray-100)",
                        padding: "0.5rem 1rem",
                        borderRadius: "1rem",
                      }}
                    >
                      {userProfile?.preferredUnits || "yards"}
                    </span>
                  </div>
                </div>

                {/* Lie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ball Lie
                  </label>
                  <select
                    value={situation.lie}
                    onChange={(e) =>
                      handleSituationChange("lie", e.target.value)
                    }
                    className="select"
                  >
                    <option value="fairway">Fairway</option>
                    <option value="rough">Rough</option>
                    <option value="bunker">Bunker</option>
                    <option value="tee">Tee</option>
                    <option value="fringe">Fringe</option>
                    <option value="cart-path">Cart Path</option>
                  </select>
                </div>

                {/* Slope */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ground Slope
                  </label>
                  <select
                    value={situation.slope}
                    onChange={(e) =>
                      handleSituationChange("slope", e.target.value)
                    }
                    className="select"
                  >
                    <option value="flat">Flat</option>
                    <option value="uphill">Uphill</option>
                    <option value="downhill">Downhill</option>
                    <option value="sidehill-above">
                      Sidehill (Ball Above Feet)
                    </option>
                    <option value="sidehill-below">
                      Sidehill (Ball Below Feet)
                    </option>
                  </select>
                </div>

                {/* Wind Speed */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🌬️ Wind Speed
                  </label>
                  <select
                    value={windSpeed}
                    onChange={(e) => setWindSpeed(e.target.value as any)}
                    className="select"
                  >
                    <option value="no-wind">No Wind</option>
                    <option value="light">Light (5 mph)</option>
                    <option value="moderate">Moderate (10 mph)</option>
                    <option value="strong">Strong (15 mph)</option>
                    <option value="very-strong">Very Strong (20+ mph)</option>
                  </select>
                </div>

                {/* Wind Direction - only show if there's wind */}
                {windSpeed !== "no-wind" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🧭 Wind Direction
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: "headwind", label: "Headwind", emoji: "⬆️" },
                        { value: "tailwind", label: "Tailwind", emoji: "⬇️" },
                        { value: "crosswind", label: "Crosswind", emoji: "↔️" }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setWindDirection(option.value as any)}
                          className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                            windDirection === option.value
                              ? "bg-blue-100 text-blue-700 border border-blue-300"
                              : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                          }`}
                        >
                          {option.emoji} {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {windDirection === "headwind" ? "Club UP for headwind" : 
                       windDirection === "tailwind" ? "Club DOWN for tailwind" : 
                       "Adjust aim for crosswind"}
                    </p>
                  </div>
                )}

                {/* Elevation Change */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ⛰️ Elevation Change (feet)
                  </label>
                  <select
                    value={elevation}
                    onChange={(e) => setElevation(e.target.value)}
                    className="select"
                  >
                    <option value="-20">-20 ft (downhill)</option>
                    <option value="-10">-10 ft (downhill)</option>
                    <option value="-5">-5 ft (slight downhill)</option>
                    <option value="0">Level</option>
                    <option value="5">+5 ft (slight uphill)</option>
                    <option value="10">+10 ft (uphill)</option>
                    <option value="20">+20 ft (uphill)</option>
                  </select>
                </div>

                {/* Weather */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weather Conditions
                  </label>
                  <select
                    value={situation.weather}
                    onChange={(e) =>
                      handleSituationChange("weather", e.target.value)
                    }
                    className="select"
                  >
                    <option value="calm">Calm</option>
                    <option value="windy">Windy</option>
                    <option value="wet">Wet</option>
                    <option value="cold">Cold</option>
                    <option value="hot">Hot</option>
                  </select>
                </div>

                {/* Pin Position */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pin Position
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {["front", "middle", "back"].map((pos) => (
                      <button
                        key={pos}
                        onClick={() => handleSituationChange("pin", pos)}
                        className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                          situation.pin === pos
                            ? "bg-green-100 text-green-700 border border-green-300"
                            : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        {pos.charAt(0).toUpperCase() + pos.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => getAdvice(windSpeed, windDirection, elevation, situation.distance)}
                  disabled={isAnalyzing || !userProfile?.golfBag?.length}
                  className="btn-feature w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                >
                  {isAnalyzing ? (
                    <>
                      <Target size={18} className="animate-pulse" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <Brain size={18} />
                      <span>{hasAnalyzedOnce ? "Re-analyze Shot" : "AI Shot Analysis"}</span>
                    </>
                  )}
                </button>
                
                {hasAnalyzedOnce && !isAnalyzing && (
                  <p className="text-xs text-green-600 mt-2 text-center">
                    {inputsChanged ? "Updating recommendation..." : "Tip: Change any input above and the recommendation updates automatically"}
                  </p>
                )}

                {!userProfile?.golfBag?.length && (
                  <p className="text-xs text-orange-600 mt-2">
                    💡 Set up your club distances first for AI-powered advice
                  </p>
                )}
              </div>
            </div>

            {/* Quick Situations */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Situations
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setSituation({
                      lie: "bunker",
                      slope: "flat",
                      weather: "calm",
                      distance: 30,
                      pin: "middle",
                      hazards: [],
                    });
                    setWindSpeed("no-wind");
                    setElevation("0");
                  }}
                  className="btn-secondary text-left p-3"
                >
                  <div className="font-medium">Greenside Bunker</div>
                  <div className="text-xs text-gray-500">30 yards</div>
                </button>
                <button
                  onClick={() => {
                    setSituation({
                      lie: "rough",
                      slope: "uphill",
                      weather: "windy",
                      distance: 175,
                      pin: "back",
                      hazards: [],
                    });
                    setWindSpeed("moderate");
                    setWindDirection("headwind");
                    setElevation("10");
                  }}
                  className="btn-secondary text-left p-3"
                >
                  <div className="font-medium">Uphill Rough + Wind</div>
                  <div className="text-xs text-gray-500">175 yards, headwind</div>
                </button>
                <button
                  onClick={() => {
                    setSituation({
                      lie: "fairway",
                      slope: "downhill",
                      weather: "calm",
                      distance: 140,
                      pin: "front",
                      hazards: ["water"],
                    });
                    setWindSpeed("no-wind");
                    setElevation("-10");
                  }}
                  className="btn-secondary text-left p-3"
                >
                  <div className="font-medium">Downhill to Water</div>
                  <div className="text-xs text-gray-500">140 yards, -10ft</div>
                </button>
                <button
                  onClick={() => {
                    setSituation({
                      lie: "tee",
                      slope: "flat",
                      weather: "windy",
                      distance: 280,
                      pin: "middle",
                      hazards: [],
                    });
                    setWindSpeed("strong");
                    setWindDirection("headwind");
                    setElevation("0");
                  }}
                  className="btn-secondary text-left p-3"
                >
                  <div className="font-medium">Strong Headwind Tee</div>
                  <div className="text-xs text-gray-500">280 yards, 15mph headwind</div>
                </button>
              </div>
            </div>
          </div>

          {/* Advice Display */}
          <div>
            <div className="card">
              {isAnalyzing ? (
                /* Futuristic AI Analysis Animation */
                <div className="p-8 relative overflow-hidden min-h-[400px] flex flex-col items-center justify-center">
                  {/* Background Grid Effect */}
                  <div 
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: `
                        linear-gradient(rgba(34, 197, 94, 0.3) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(34, 197, 94, 0.3) 1px, transparent 1px)
                      `,
                      backgroundSize: '20px 20px',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                  
                  {/* Scanning Lines */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(transparent 0%, rgba(34, 197, 94, 0.1) 50%, transparent 100%)',
                      backgroundSize: '100% 200%',
                      animation: 'scanline 1.5s linear infinite'
                    }}
                  />
                  
                  {/* Central Brain Icon with Glow */}
                  <div className="relative mb-6">
                    {/* Outer Pulse Ring */}
                    <div 
                      className="absolute inset-0 rounded-full bg-green-500"
                      style={{
                        transform: 'scale(2.5)',
                        opacity: 0.1,
                        animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite'
                      }}
                    />
                    {/* Middle Pulse Ring */}
                    <div 
                      className="absolute inset-0 rounded-full bg-green-400"
                      style={{
                        transform: 'scale(1.8)',
                        opacity: 0.2,
                        animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite 0.3s'
                      }}
                    />
                    {/* Brain Container */}
                    <div 
                      className="relative w-20 h-20 rounded-full flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, #22c55e 0%, #10b981 50%, #059669 100%)',
                        boxShadow: '0 0 40px rgba(34, 197, 94, 0.5), 0 0 80px rgba(34, 197, 94, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <Brain size={40} className="text-white" style={{ animation: 'pulse 1s ease-in-out infinite' }} />
                    </div>
                    {/* Orbiting Dots */}
                    <div 
                      className="absolute inset-0"
                      style={{ animation: 'spin 3s linear infinite' }}
                    >
                      <div className="absolute -top-2 left-1/2 w-2 h-2 bg-green-400 rounded-full" style={{ transform: 'translateX(-50%)', boxShadow: '0 0 10px rgba(34, 197, 94, 0.8)' }} />
                    </div>
                    <div 
                      className="absolute inset-0"
                      style={{ animation: 'spin 3s linear infinite reverse' }}
                    >
                      <div className="absolute -bottom-2 left-1/2 w-2 h-2 bg-emerald-300 rounded-full" style={{ transform: 'translateX(-50%)', boxShadow: '0 0 10px rgba(52, 211, 153, 0.8)' }} />
                    </div>
                  </div>
                  
                  {/* Status Text */}
                  <div className="text-center relative z-10 mb-6">
                    <h3 
                      className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent mb-2"
                      style={{ letterSpacing: '0.05em' }}
                    >
                      AI ANALYSIS IN PROGRESS
                    </h3>
                    <p 
                      className="text-green-600 font-medium"
                      style={{ 
                        minHeight: '24px',
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      {analysisMessages[analysisPhase]}
                    </p>
                  </div>
                  
                  {/* Progress Indicators */}
                  <div className="flex items-center gap-3 relative z-10">
                    {analysisMessages.map((_, index) => (
                      <div 
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          index <= analysisPhase 
                            ? 'bg-green-500 scale-110' 
                            : 'bg-gray-300 scale-100'
                        }`}
                        style={{
                          boxShadow: index <= analysisPhase ? '0 0 8px rgba(34, 197, 94, 0.6)' : 'none'
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Data Points Animation */}
                  <div className="flex items-center gap-6 mt-6 text-xs text-gray-500 relative z-10">
                    <div className="flex items-center gap-1">
                      <Activity size={14} className="text-green-500" style={{ animation: 'pulse 0.8s ease-in-out infinite' }} />
                      <span>Wind: {windSpeed === 'no-wind' ? '0' : windSpeed === 'light' ? '5' : windSpeed === 'moderate' ? '10' : windSpeed === 'strong' ? '15' : '20'} mph</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Zap size={14} className="text-amber-500" style={{ animation: 'pulse 0.8s ease-in-out infinite 0.2s' }} />
                      <span>Elev: {elevation}ft</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Cpu size={14} className="text-blue-500" style={{ animation: 'pulse 0.8s ease-in-out infinite 0.4s' }} />
                      <span>{situation.distance}yd</span>
                    </div>
                  </div>
                  
                  {/* CSS Keyframes */}
                  <style>{`
                    @keyframes scanline {
                      0% { background-position: 0% 0%; }
                      100% { background-position: 0% 100%; }
                    }
                    @keyframes spin {
                      from { transform: rotate(0deg); }
                      to { transform: rotate(360deg); }
                    }
                    @keyframes ping {
                      75%, 100% { transform: scale(2.5); opacity: 0; }
                    }
                  `}</style>
                </div>
              ) : advice ? (
                <div className="overflow-hidden">
                  {/* Hero Section with Gradient */}
                  <div className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-6 text-white relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                      }} />
                    </div>
                    
                    <div className="relative">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <Target size={20} className="text-white" />
                          </div>
                          <h2 className="text-xl font-bold">Shot Recommendation</h2>
                        </div>
                        <button
                          onClick={() => { setAdvice(null); setHasAnalyzedOnce(false); setInputsChanged(false); }}
                          className="px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg transition-colors flex items-center gap-1 text-sm font-medium"
                        >
                          ← New Shot
                        </button>
                      </div>
                      
                      {/* Effective Yardage Hero */}
                      {(() => {
                        const windMph = windSpeed === "no-wind" ? 0 : windSpeed === "light" ? 5 : windSpeed === "moderate" ? 10 : windSpeed === "strong" ? 15 : 20;
                        const elevFt = parseInt(elevation) || 0;
                        let windAdj = 0;
                        if (windDirection === "headwind") windAdj = Math.round(windMph * 0.8);
                        else if (windDirection === "tailwind") windAdj = -Math.round(windMph * 0.5);
                        const elevAdj = Math.round(elevFt * 0.33);
                        const effectiveYds = situation.distance + windAdj + elevAdj;
                        const hasAdjustments = windAdj !== 0 || elevAdj !== 0;

                        return hasAdjustments ? (
                          <div className="mt-4 bg-white/15 backdrop-blur-sm rounded-xl p-4">
                            <div className="text-center">
                              <div className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">Effective Yardage</div>
                              <div className="text-4xl sm:text-5xl font-black text-white">{effectiveYds}</div>
                              <div className="text-white/60 text-xs mt-2 flex flex-wrap justify-center gap-1 sm:gap-2">
                                <span>{situation.distance} yds target</span>
                                {windAdj !== 0 && <span>• {windAdj > 0 ? '+' : ''}{windAdj} wind</span>}
                                {elevAdj !== 0 && <span>• {elevAdj > 0 ? '+' : ''}{elevAdj} elev</span>}
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Main Club Recommendation */}
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-4">
                        <div className="bg-white text-green-700 px-4 sm:px-6 py-3 rounded-2xl font-bold text-xl sm:text-2xl shadow-lg truncate max-w-[200px] sm:max-w-none">
                          {getClubDisplayName(advice.recommendedClub)}
                        </div>
                        <div className="text-white/90">
                          <div className="text-sm font-medium">Target Distance</div>
                          <div className="text-xl sm:text-2xl font-bold">{situation.distance} {userProfile?.preferredUnits || "yards"}</div>
                        </div>
                      </div>
                      
                      {/* Alternative Clubs */}
                      {advice.alternativeClubs.length > 0 && (
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <span className="text-white/70 text-sm">Alternatives:</span>
                          <div className="flex flex-wrap gap-2">
                            {advice.alternativeClubs.map((club) => (
                              <span
                                key={club}
                                className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm font-medium"
                              >
                                {getClubDisplayName(club)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Setup & Tips Section */}
                  <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
                    {/* Setup Adjustments */}
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Settings size={16} className="text-emerald-600" />
                        </div>
                        Setup Adjustments
                      </h3>
                      <div className="grid gap-3">
                        {Object.entries(advice.adjustments).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                            >
                              <div className="font-semibold text-gray-900 capitalize text-sm mb-1">
                                {key.replace(/([A-Z])/g, " $1")}
                              </div>
                              <div className="text-gray-600 text-sm">
                                {stripMarkdown(String(value))}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Pro Tips */}
                    {advice.tips.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Lightbulb size={16} className="text-amber-600" />
                          </div>
                          Pro Tips
                        </h3>
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                          <div className="space-y-3">
                            {advice.tips.map((tip, index) => (
                              <div
                                key={index}
                                className="flex items-start gap-3"
                              >
                                <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                  {index + 1}
                                </div>
                                <p className="text-gray-700 text-sm">{stripMarkdown(tip)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Target size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">Ready for advice</p>
                  <p className="text-sm">
                    Set your situation and get personalized shot recommendations
                  </p>
                </div>
              )}
            </div>

            {/* Situation Summary */}
            {advice && (
              <div className="card p-4 mt-6">
                <h3 className="font-medium text-gray-900 mb-3">
                  Current Situation
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="capitalize">{situation.lie}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Wind size={16} className="text-gray-400" />
                    <span className="capitalize">{situation.weather}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Flag size={16} className="text-gray-400" />
                    <span className="capitalize">{situation.pin} pin</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <AlertTriangle size={16} className="text-gray-400" />
                    <span className="capitalize">{situation.slope}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ShotAdvisor;
